<script lang="ts" setup>
import { useField, useForm } from 'vee-validate';
import { useId } from '@tools/composables/id';
import {
  useInstellingenHaalOfferteDefaultInstellingenGebruikerOp,
  useInstellingenWerkOfferteDefaultInstellingenGebruikerBij,
} from '@services/crm-public/orval-api/instellingen-api/instellingen-api';
import { computed, watch } from 'vue';
import { useWatchErrorHandler } from '@errors/useErrorHandler';
import FieldRowV2 from '@shared/form/FieldRowV2/FieldRowV2.vue';
import FieldGroup from '@shared/form/FieldRowV2/FieldGroup.vue';
import VRadioField from '@shared/form/VRadioField/VRadioField.vue';
import { jaNeeOptions } from '@shared/form/VRadioField/JaNeeOptions';
import FadeSlideTransition from '@shared/animation/transitions/FadeSlideTransition/FadeSlideTransition.vue';
import { watchDebounced } from '@vueuse/core';
import VContentBox from '@shared/component/VContentBox/VContentBox.vue';

interface GebruikerInstellingenFormValues {
  toelichtingOnderdrukken: boolean;
  defaultBccOntvangen: boolean;
}

const { values, resetForm } = useForm<GebruikerInstellingenFormValues>();
const ids = useId('offerte-defaults-gebruiker', [
  'toelichting-onderdrukken',
  'default-bcc-ontvangen',
]);

const {
  data: gebruikerInstellingen,
  error: gebruikerInstellingenError,
  isSuccess: gebruikerInstellingenOpgehaald,
} = useInstellingenHaalOfferteDefaultInstellingenGebruikerOp(
  computed(() => ({})),
  {
    query: {
      select: ({ result }) => result,
    },
  },
);

useWatchErrorHandler(gebruikerInstellingenError);

watch(
  gebruikerInstellingen,
  (nieuweGebruikerInstellingen) => {
    if (nieuweGebruikerInstellingen) {
      resetForm({
        values: {
          toelichtingOnderdrukken: nieuweGebruikerInstellingen.toelichtingOnderdrukken,
          defaultBccOntvangen: nieuweGebruikerInstellingen.defaultBccOntvangen,
        },
      });
    }
  },
  { immediate: true },
);

const { value: toelichtingOnderdrukken } = useField<boolean>('toelichtingOnderdrukken');
const { value: defaultBccOntvangen } = useField<boolean>('defaultBccOntvangen');

const { mutate: werkGebruikerInstellingenBij, error: gebruikerInstellingenOpslaanError } =
  useInstellingenWerkOfferteDefaultInstellingenGebruikerBij();

useWatchErrorHandler(gebruikerInstellingenOpslaanError);

function save() {
  if (!gebruikerInstellingenOpgehaald.value) {
    return;
  }
  werkGebruikerInstellingenBij({
    data: {
      toelichtingOnderdrukken: values.toelichtingOnderdrukken,
      defaultBccOntvangen: values.defaultBccOntvangen,
    },
  });
}
watchDebounced(values, save, { debounce: 500, deep: true });
</script>

<template>
  <VContentBox title="Instellingen op gebruikersniveau">
    <div class="flex flex-col gap-6">
      <FadeSlideTransition direction="down" group>
        <form>
          <FieldGroup>
            <FieldRowV2
              label="Toelichting maatschappij popup onderdrukken"
              :label-for="ids['toelichting-onderdrukken']"
              :label-in-bold="false"
              tooltip="Bij het selecteren van een maatschappij tijdens het offreren, krijg je een popup om jouw keuze voor een bepaalde maatschappij toe te lichten. Je kunt dit hier uitzetten."
              :force-label-text-align-left="true"
            >
              <VRadioField
                v-model:value="toelichtingOnderdrukken"
                :id="ids['toelichting-onderdrukken']"
                name="toelichtingOnderdrukken"
                :options="jaNeeOptions"
              />
            </FieldRowV2>

            <FieldRowV2
              label="Default een BCC ontvangen van elke verzonden offerte"
              :label-for="ids['default-bcc-ontvangen']"
              :label-in-bold="false"
              tooltip="Bij offertes die je aan jouw klant verstuurt, wordt standaard ingesteld dat je een bcc wilt ontvangen."
              :force-label-text-align-left="true"
            >
              <VRadioField
                v-model:value="defaultBccOntvangen"
                :id="ids['default-bcc-ontvangen']"
                name="defaultBccOntvangen"
                :options="jaNeeOptions"
              />
            </FieldRowV2>
          </FieldGroup>
        </form>
      </FadeSlideTransition>
    </div>
  </VContentBox>
</template>
